import React, { useEffect, useState } from 'react';
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Pie } from 'react-chartjs-2';

// Chart.js 플러그인 등록
ChartJS.register(ArcElement, Title, Tooltip, Legend, ChartDataLabels);

class Params {
    labelKey: string = ''; // 라벨로 사용할 key
    colors: string[] = []; // 데이터 색상
    valueFormat: Function = (value: any) => value; // 툴팁 값 형식 지정
    labelFormat: Function = (value: any) => value; // 라벨 형식 지정
    keys: string[] = []; // 표시할 데이터 키
    data: any = { labels: [], datasets: [] };
}

function ChartPie(props: any) {
    const [params, setParams] = useState(new Params());

    useEffect(() => {
        const initParams = new Params();

        initParams.labelKey = props.labelKey || '';

        if (props.labelFormat) {
            initParams.labelFormat = props.labelFormat;
        }
        if (props.valueFormat) {
            initParams.valueFormat = props.valueFormat;
        }

        const data = props.data || [];
        const keys = props.keys || []; // 표시할 데이터 키들

        initParams.colors = props.colors || data.map((_, index) => defaultColors[index % defaultColors.length]); // colors가 없으면 자동 생성

        // Chart.js의 데이터 형식으로 변환
        initParams.data = {
            labels: data.map((item: any) => initParams.labelFormat(item[initParams.labelKey])), // 라벨 생성
            datasets: keys.map((key: string, index: number) => ({
                label: props.labelFormat ? props.labelFormat(key) : key, // 각 데이터 집합의 라벨
                data: data.map((item: any) => item[key]), // 각 데이터 키에 해당하는 값
                backgroundColor: initParams.colors,
                borderColor: initParams.colors.map(color => color.replace('1)', '0.8)')), // 약간 투명한 테두리
                borderWidth: 2,
            })),
        };

        setParams(() => ({
            ...initParams,
        }));
    }, [props]);

    // 기본 색상 팔레트
    const defaultColors = [
        'rgba(231, 76, 60, 0.8)',    // 빨강
        'rgba(241, 196, 15, 0.8)',   // 노랑
        'rgba(46, 204, 113, 0.8)',   // 초록
        'rgba(52, 152, 219, 0.8)',   // 파랑
        'rgba(155, 89, 182, 0.8)',   // 보라
        'rgba(230, 126, 34, 0.8)',   // 주황
        'rgba(102, 205, 170, 0.8)',  // 연두색
        'rgba(255, 20, 147, 0.8)',   // 진분홍색
        'rgba(169, 169, 169, 0.8)',  // 회색
    ];

    // 색상 자동 생성 함수
    const generateColors = (keys: string[]) => {
        return keys.map((_, index) => defaultColors[index % defaultColors.length]);
    };

    // 차트 옵션 (데이터 라벨 항상 표시)
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // 범례 숨김
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const value = context.raw;
                        const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return ` ${context.label}: ${params.valueFormat(value)} (${percentage}%)`;
                    },
                },
            },
            datalabels: {
                display: true,
                color: 'white',
                font: {
                    size: 12,
                    weight: 'bold' as const,
                },
                formatter: (value: any, context: any) => {
                    const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                    const label = context.chart.data.labels[context.dataIndex];
                    return `${label}\n${percentage}%\n${params.valueFormat(value)}`;
                },
                textAlign: 'center' as const,
            },
        },
    };

    return <Pie data={params.data} options={options} />;
}

export default ChartPie;